import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { surJournal, surLiaison } from '../api/client';
import { STATUT_LABEL } from '../lib/format';
import { T } from '../i18n';

export function Badge({ statut, libelle }: { statut: string; libelle?: string }) {
  const pulse = statut === 'retard' || statut === 'non_restitue';
  return (
    <span className={`badge ${statut}`}>
      {pulse && <i className="pt" />}
      {libelle ?? STATUT_LABEL[statut] ?? statut}
    </span>
  );
}

export function Carte({ titre, action, children, className = '', pad = true }:
  { titre?: ReactNode; action?: ReactNode; children: ReactNode; className?: string; pad?: boolean }) {
  return (
    <section className={`carte ${className}`}>
      {titre && (
        <div className="carte-t">
          <h3 style={{ flex: 1 }}>{titre}</h3>
          {action}
        </div>
      )}
      <div className={pad ? 'carte-p' : ''}>{children}</div>
    </section>
  );
}

export function Kpi({ libelle, valeur, detail, className = '', to }:
  { libelle: string; valeur: ReactNode; detail?: ReactNode; className?: string; to?: string }) {
  const contenu = (
    <>
      <div className="micro">{libelle}</div>
      <div className="v">{valeur}</div>
      {detail && <div className="d">{detail}</div>}
    </>
  );
  // Un chiffre qui parle des voitures doit mener aux voitures : c'est le chemin
  // que cherche quelqu'un qui vient de lire « taux d'utilisation : 33 % ».
  if (to) {
    return (
      <Link to={to} className={`carte carte-p kpi kpi-lien ${className}`}>
        {contenu}
        <span className="kpi-fleche" aria-hidden="true">›</span>
      </Link>
    );
  }
  return <div className={`carte carte-p kpi ${className}`}>{contenu}</div>;
}

export function Bandeau({ type = 'info', titre, children }:
  { type?: 'info' | 'ok' | 'warn' | 'ko' | 'accent'; titre?: string; children?: ReactNode }) {
  return (
    <div className={`bandeau ${type}`} role={type === 'ko' ? 'alert' : undefined}>
      <div>{titre && <b className="bandeau-t">{titre}</b>}{children}</div>
    </div>
  );
}

/**
 * Le nom d'un client, avec son téléphone à côté — et cliquable.
 *
 * Au comptoir, la question qui suit « qui est-ce ? » est toujours « comment je
 * le joins ? ». Le numéro vivait uniquement dans la fiche client : pour
 * rappeler quelqu'un dont la voiture n'est pas rentrée, il fallait quitter sa
 * liste, ouvrir Clients, chercher, revenir. Sur un téléphone, `tel:` lance
 * l'appel ; sur l'ordinateur du comptoir, le numéro est au moins lisible et
 * copiable sans changer d'écran.
 */
export function ClientTel({ nom, telephone, compact }:
  { nom: string; telephone?: string | null; compact?: boolean }) {
  if (!telephone) return <>{nom}</>;
  return (
    <>
      {nom}
      <a href={`tel:${telephone.replace(/\s+/g, '')}`}
         className={compact ? 'tel mini' : 'tel'}
         onClick={(e) => e.stopPropagation()}
         title={T.ui.appeler(nom)}>
        <span aria-hidden="true">📞</span> {telephone}
      </a>
    </>
  );
}

export function Champ({ label, aide, erreur, ok, children }:
  { label: string; aide?: ReactNode; erreur?: ReactNode; ok?: boolean; children: ReactNode }) {
  return (
    <div className={`champ ${erreur ? 'ko' : ok ? 'ok' : ''}`}>
      <label>{label}</label>
      {children}
      {erreur ? <span className="err">{erreur}</span> : aide ? <span className="aide">{aide}</span> : null}
    </div>
  );
}

export function Vide({ titre, children, action }: { titre: string; children?: ReactNode; action?: ReactNode }) {
  return <div className="vide"><h3>{titre}</h3>{children && <p>{children}</p>}{action && <div style={{ marginTop: 14 }}>{action}</div>}</div>;
}

export function Squelette({ lignes = 4 }: { lignes?: number }) {
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {Array.from({ length: lignes }).map((_, i) => (
        <div key={i} className="squelette" style={{ width: `${100 - i * 7}%` }} />
      ))}
    </div>
  );
}

export function Panneau({ titre, onFermer, children, largeur }:
  { titre: ReactNode; onFermer: () => void; children: ReactNode; largeur?: number }) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === 'Escape' && onFermer();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onFermer]);
  return (
    <div className="voile" onClick={onFermer}>
      <div className="panneau" style={largeur ? { width: `min(${largeur}px,100%)` } : undefined} onClick={(e) => e.stopPropagation()}>
        <div className="entete">
          <div className="titres"><h2>{titre}</h2></div>
          <button className="btn btn-f" onClick={onFermer}>{T.commun.actions.fermer}</button>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * Une fenetre par-dessus l'ecran.
 *
 * Elle est posee dans le CORPS de la page, jamais la ou le bouton se trouve.
 * La barre laterale est en `position: sticky`, ce qui cree un contexte
 * d'empilement : une modale ouverte depuis la barre y restait prisonniere, et
 * le contenu de la page se dessinait PAR-DESSUS elle. Constate sur « Mon mot
 * de passe » — la moitie basse de la fenetre etait recouverte par une ligne de
 * la file du jour, boutons compris. Le meme piege attend n'importe quelle
 * future modale ouverte depuis un en-tete colle ou une carte transformee.
 */
export function Modale({ titre, onFermer, children }: { titre: ReactNode; onFermer: () => void; children: ReactNode }) {
  return createPortal(
    <div className="voile centre" onClick={onFermer}>
      <div className="modale" onClick={(e) => e.stopPropagation()}>
        <h2 style={{ fontSize: 18, marginBottom: 12 }}>{titre}</h2>
        {children}
      </div>
    </div>,
    document.body,
  );
}

/** Bandeau hors ligne — le vrai filet du terrain. */
/**
 * Deux pannes differentes, deux messages differents.
 *
 * `navigator.onLine` ne voit que la carte reseau de l'appareil : il reste VRAI
 * quand le Wi-Fi marche mais que le serveur, lui, ne repond plus. C'etait le cas
 * le plus frequent, et le moins visible — l'ecran restait en squelette gris,
 * indefiniment, sans un mot.
 */
export function IndicateurReseau() {
  const [enLigne, setEnLigne] = useState(navigator.onLine);
  const [serveurMuet, setServeurMuet] = useState(false);
  const [tracePerdue, setTracePerdue] = useState(false);
  useEffect(() => surJournal(setTracePerdue), []);
  useEffect(() => {
    const on = () => setEnLigne(true), off = () => setEnLigne(false);
    window.addEventListener('online', on); window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  useEffect(() => surLiaison(setServeurMuet), []);

  /*
   * Une operation a reussi mais n'a pas laisse de trace. C'est le silence le
   * plus couteux du systeme : sans journal, il n'y a aucune preuve de ce qui a
   * ete fait — et c'est precisement ce qui remplace la facture ici.
   */
  if (tracePerdue) {
    return (
      <div style={{ marginBottom: 14 }}>
        <Bandeau type="ko" titre={T.ui.traceTitre}>
          {T.ui.traceDebut}<b>{T.ui.traceGras}</b>{T.ui.traceSuite}
          <b>{T.ui.tracePrevenez}</b>{T.ui.traceFin}
        </Bandeau>
      </div>
    );
  }

  if (!enLigne) {
    return <div className="hors-ligne">{T.ui.horsLigne}</div>;
  }
  if (!serveurMuet) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <Bandeau type="ko" titre={T.ui.serveurTitre}>
        {T.ui.serveurTexte}
        <b>{T.ui.serveurRienPerdu}</b>{T.ui.serveurFin}
        <div className="actions" style={{ marginTop: 12 }}>
          <button className="btn btn-p" onClick={() => window.location.reload()}>{T.commun.actions.reessayer}</button>
        </div>
      </Bandeau>
    </div>
  );
}
